import Loading from '@/components/Atoms/Loading.tsx'
import { BareLayout } from '@/components/Molecules/BareLayout.tsx'
import Layout from '@/components/Molecules/Layout.tsx'
import ApplicationProvider from '@/components/Pages/Application/ApplicationProvider'
import Login from '@/components/Pages/Auth/Login'
import NotFound from '@/components/Pages/NotFound'
import { useAuth } from '@/hooks/useAuth'
import { AutonomousVehicleLayout } from '@/modules/applications/modules/automated-vehicles/components/AutonomousVehicleLayout.tsx'
import type React from 'react'
import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  redirect,
  Route,
  RouterProvider,
} from 'react-router'
import { Toaster } from 'sonner'
import { AppUpdateBanner } from './components/Molecules/AppUpdateBanner'
import ErrorBoundary from './components/Molecules/ErrorBoundary'
import { RootErrorBoundary } from './components/Molecules/RootErrorBoundary'
import { BrokerStepsProvider } from './components/Pages/Application/Broker/BrokerSteps.tsx'
import { MotorCarrierStepsProvider } from './modules/applications/modules/certificate/hooks/MotorCarrierSteps'
import HelpDocumentProvider from './components/Pages/HelpDocuments/HelpDocumentProvider.tsx'
import { BusinessEntityProvider } from './hooks/business-entity.tsx'
import AppConfigProvider from './hooks/useAppConfig.tsx'
import AuthProvider from './hooks/useAuth'
import { ErrorProvider } from './hooks/useError'
import { ApplicationsLayout } from './modules/applications/components/ApplicationsLayout.tsx'
import { LicensingAgencyApplicationLayout } from './modules/applications/modules/licensing-agency/components/LicensingAgencyApplicationLayout.tsx'
import { RenewalLayout } from './modules/companies/components/renewal/RenewalLayout.tsx'
import { CasesLayout } from './modules/cases/components/CasesLayout.tsx'
import { CaseDetails } from './modules/cases/routes/CaseDetails.tsx'
import { CompaniesLayout } from './modules/companies/components/CompaniesLayout.tsx'
import CompanyDetails from './modules/companies/routes/CompanyDetails.tsx'
import { CredentialingLayout } from './modules/credentialing/components/CredentialingLayout.tsx'
import { EditQueueLayout } from './components/Pages/EditQueueLayout.tsx'
import { LicensingAgenciesLayout } from './modules/licensing-agencies/components/LicensingAgenciesLayout.tsx'
import { LicensingAgencyLayout } from './modules/licensing-agencies/components/LicensingAgencyLayout.tsx'
import { PaymentProcessLayout } from './modules/payments/components/PaymentProcessLayout.tsx'
import { PaymentsLayout } from './modules/payments/components/PaymentsLayout.tsx'
import { TicketsLayout } from './modules/tickets/components/TicketsLayout.tsx'
import { TicketDetails } from './modules/tickets/routes/TicketDetails.tsx'
import { TruckStopLayout } from './modules/truckstop/components/TruckStopLayout.tsx'
import TruckStopCompanyDetails from './modules/truckstop/routes/TruckStopCompanyDetails.tsx'
import { InsuranceUsersLayout } from './modules/insurance/users/components/InsuranceUsersLayout.tsx'
import { InsurersLayout } from './modules/insurance/insurers/components/InsurersLayout.tsx'
import { InsurerLayout } from './modules/insurance/insurers/components/InsurerLayout.tsx'
import { UsersLayout } from './modules/users/components/UsersLayout.tsx'
import { buildPath, type RouteKey, routes } from './routes'
import { lazyImport } from './lib/lazyImport'
import { VinLookupLayout } from './modules/companies/routes/vin-lookup/VinLookupLayout.tsx'

const SetupPasswordModal = lazyImport(
  () => import('@/components/Pages/Profile/SetupPasswordModal')
)

const AutonomousVehicleStepGuard = lazyImport(
  () =>
    import(
      '@/modules/applications/modules/automated-vehicles/routes/AutonomousVehicleStepGuard'
    )
)

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route element={<BusinessEntityProvider />}>
        {/* Public/Unauthenticated routes with their own full-page chrome (no shared topbar) */}
        <Route element={<BareLayout />}>
          {route('/signup', 'Sign Up')}
          {route('/email-sent', 'Email Sent')}
        </Route>

        {/* Public/Unauthenticated routes */}
        <Route element={<Layout noSidebar />}>
          {route('/forgot-password', 'Forgot Password')}
          {route('/reset-password', 'Reset Password')}
          {route('/confirm-email', 'Email Confirmed')}
          {route('/confirm-new-email', 'Confirm New Email')}
          {route('/confirm-new-secondary-email', 'Confirm Secondary Email')}

          {/* Truck Stop */}
          <Route path={path('/truckstop')} element={<TruckStopLayout />}>
            {index('/truckstop', 'Truck Stop')}
            {route('/truckstop/search', 'Truck Stop - Search')}

            <Route
              path={path('/truckstop/companies/:businessEntityId')}
              element={<TruckStopCompanyDetails />}
              handle={{ title: 'Truck Stop - Company Details' }}
            >
              {index(
                '/truckstop/companies/:businessEntityId',
                'Truck Stop - Company Details'
              )}
              {route(
                '/truckstop/companies/:businessEntityId/certificate',
                'Truck Stop - Certificate'
              )}
              {route(
                '/truckstop/companies/:businessEntityId/automated-motor-vehicles',
                'Truck Stop - Automated Motor Vehicles'
              )}
            </Route>

            {route('/truckstop/complaints', 'Truck Stop - Complaints')}
            {route('/truckstop/complaints/file', 'Truck Stop - File Complaint')}
            {route(
              '/truckstop/complaints/submitted',
              'Truck Stop - Complaint Submitted'
            )}
            {route('/truckstop/complaints/faq', 'Truck Stop - Complaints FAQ')}
          </Route>

          {/* Legacy path — /automated-vehicles was renamed to /truckstop */}
          <Route
            path="/automated-vehicles"
            loader={() => redirect(buildPath('/truckstop'))}
          />
        </Route>

        {/* Authenticated Routes */}
        <Route element={<RequireAuth />}>
          <Route element={<Layout noSidebar />}>{index('/', 'Home')}</Route>

          {/* Sidebar Layout */}
          <Route element={<Layout />}>
            {route('/dashboard', 'Dashboard')}

            <Route path={path('/companies')} element={<CompaniesLayout />}>
              {index('/companies', 'Companies')}

              <Route
                path={path('/companies/vin-lookup')}
                element={<VinLookupLayout />}
              >
                {index('/companies/vin-lookup', 'VIN Lookup')}
                {route('/companies/vin-lookup/:id', 'VIN History')}
              </Route>

              <Route
                path={path('/companies/:businessEntityId')}
                element={<CompanyDetails />}
                handle={{ title: 'Company Details' }}
              >
                {index('/companies/:businessEntityId', 'Company Details')}
                {route(
                  '/companies/:businessEntityId/certificate',
                  'Certificate'
                )}
                {route('/companies/:businessEntityId/insurance', 'Insurance')}
                {route('/companies/:businessEntityId/hhg-tariff', 'HHG Tariff')}
                {route(
                  '/companies/:businessEntityId/automated-vehicles',
                  'Automated Vehicles'
                )}
                {route('/companies/:businessEntityId/payments', 'Payments')}
                {route('/companies/:businessEntityId/history', 'History')}
                {route('/companies/:businessEntityId/users', 'Users')}
                {route(
                  '/companies/:businessEntityId/users/:userId',
                  'User Details'
                )}
              </Route>

              <Route
                path={path('/companies/:businessEntityId/certificate/renewal')}
                element={<RenewalLayout />}
                handle={{ title: 'Certificate Renewal' }}
              >
                {index(
                  '/companies/:businessEntityId/certificate/renewal',
                  'Certificate Renewal'
                )}
                {route(
                  '/companies/:businessEntityId/certificate/renewal/registration-period',
                  'Registration Period'
                )}
                {route(
                  '/companies/:businessEntityId/certificate/renewal/confirm',
                  'Confirm Renewal'
                )}
                {route(
                  '/companies/:businessEntityId/certificate/renewal/success',
                  'Renewal Successful'
                )}
              </Route>

              <Route
                path={path(
                  '/companies/:businessEntityId/certificate/re-register'
                )}
                element={<RenewalLayout isReRegistration />}
                handle={{ title: 'Certificate Re-Registration' }}
              >
                {index(
                  '/companies/:businessEntityId/certificate/re-register',
                  'Certificate Re-Registration'
                )}
                {route(
                  '/companies/:businessEntityId/certificate/re-register/registration-period',
                  'Registration Period'
                )}
                {route(
                  '/companies/:businessEntityId/certificate/re-register/confirm',
                  'Confirm Re-Registration'
                )}
                {route(
                  '/companies/:businessEntityId/certificate/re-register/success',
                  'Re-Registration Successful'
                )}
              </Route>
            </Route>

            <Route
              path={path('/credentialing')}
              element={<CredentialingLayout />}
            >
              {index('/credentialing', 'Credentialing')}
              {route(
                '/credentialing/applications/:applicationId',
                'Application Details'
              )}
              {route(
                '/credentialing/chameleon-carriers',
                'Chameleon Carrier Queue'
              )}
              {route(
                '/credentialing/chameleon-carriers/:chameleonQueueId',
                'Chameleon Carrier'
              )}
              {route('/credentialing/vehicle-review', 'Vehicle Review')}
              {route(
                '/credentialing/vehicle-review/:vehicleReviewRequestId',
                'Vehicle Review Request'
              )}
            </Route>
            <Route path="edit-queue" element={<EditQueueLayout />}>
              {index('/edit-queue', 'Edit Queue')}
              {route(
                '/edit-queue/:certificateDataReviewId',
                'Certificate Data Review'
              )}
            </Route>
            {route('/bounced-emails', 'Bounced Emails')}

            <Route
              path={path('/cases')}
              element={<CasesLayout />}
              handle={{ title: 'Cases' }}
            >
              {index('/cases', 'Cases')}
              {route('/cases/nodd-queue', 'Cases - NODD Queue')}
              {route('/cases/final-order-queue', 'Cases - Final Order Queue')}
              <Route
                path={path('/cases/:caseId')}
                element={<CaseDetails />}
                handle={{ title: 'Case Details' }}
              >
                {index('/cases/:caseId', 'Case Details')}
                {route('/cases/:caseId/complaint', 'Complaint Details')}
                {route('/cases/:caseId/investigation', 'Investigation')}
                {route('/cases/:caseId/violations', 'Violations')}
                {route('/cases/:caseId/notices', 'Notices')}
                {route('/cases/:caseId/history', 'History')}
              </Route>
            </Route>

            <Route
              path={path('/payments')}
              element={<PaymentsLayout />}
              handle={{ title: 'Payments' }}
            >
              {index('/payments', 'Payments')}
              {route('/payments/:paymentId', 'Payment Details')}
              <Route
                path={path('/payments/process')}
                element={<PaymentProcessLayout />}
                handle={{ title: 'Process Payment' }}
              >
                {index('/payments/process', 'Process Payment')}
                {route('/payments/process/amounts', 'Payment Amounts')}
                {route('/payments/process/confirm', 'Confirm Payment')}
                {route('/payments/process/success', 'Payment Successful')}
                {route('/payments/process/error')}
              </Route>
            </Route>

            <Route
              path={path('/tickets')}
              element={<TicketsLayout />}
              handle={{ title: 'Tickets' }}
            >
              {index('/tickets', 'Tickets')}
              {route('/tickets/new', 'New Ticket')}
              <Route
                path={path('/tickets/:ticketId')}
                element={<TicketDetails />}
                handle={{ title: 'Ticket Details' }}
              >
                {index('/tickets/:ticketId', 'Ticket Details')}
              </Route>
            </Route>

            <Route
              path={path('/insurance/users')}
              element={<InsuranceUsersLayout />}
              handle={{ title: 'Insurance Users' }}
            >
              {index('/insurance/users', 'Insurance Users')}
            </Route>

            <Route
              path={path('/insurance/insurers')}
              element={<InsurersLayout />}
              handle={{ title: 'Insurers' }}
            >
              {index('/insurance/insurers', 'Insurers')}
              <Route
                path={path('/insurance/insurers/:licenseNumber')}
                element={<InsurerLayout />}
                handle={{ title: 'Insurer Details' }}
              >
                {index('/insurance/insurers/:licenseNumber', 'Insurer Details')}
                {route(
                  '/insurance/insurers/:licenseNumber/users',
                  'Insurer Users'
                )}
                {route(
                  '/insurance/insurers/:licenseNumber/companies',
                  'Insurer Companies'
                )}
              </Route>
            </Route>

            {route('/profile', 'Profile')}
            {route('/profile/:activeTab', 'Profile')}

            <Route
              path={path('/users')}
              element={<UsersLayout />}
              handle={{ title: 'Users' }}
            >
              {index('/users', 'Users')}
              {route('/users/:userId', 'User Details')}
            </Route>

            <Route
              path={path('/licensing-agencies')}
              element={<LicensingAgenciesLayout />}
            >
              {route('/licensing-agencies', 'Licensing Agencies')}
              <Route
                path={path('/licensing-agencies/:agencyId')}
                element={<LicensingAgencyLayout />}
              >
                {index(
                  '/licensing-agencies/:agencyId',
                  'Licensing Agency Details'
                )}
                {route(
                  '/licensing-agencies/:agencyId/companies',
                  'Licensing Agency Details - Authorized Companies'
                )}
                {route(
                  '/licensing-agencies/:agencyId/users',
                  'Licensing Agency Details - Users'
                )}
                {route(
                  '/licensing-agencies/:agencyId/users/:userId',
                  'Licensing Agency Details - User Details'
                )}
                {route(
                  '/licensing-agencies/:agencyId/history',
                  'Licensing Agency Details - History'
                )}
              </Route>
            </Route>

            <Route
              path={path('/help-documents')}
              element={<HelpDocumentProvider />}
              handle={{ title: 'Help Documents' }}
            >
              {index('/help-documents', 'Help Documents')}
            </Route>
          </Route>

          {/* NO Sidebar Layout */}
          <Route element={<Layout noSidebar />}>
            <Route
              path={path('/applications')}
              element={<ApplicationsLayout />}
              handle={{ title: 'Applications' }}
            >
              {index('/applications', 'Applications')}

              {/* TxDMV Certificates Application */}
              <Route
                path={path('/applications/certificate')}
                element={<ApplicationProvider />}
                handle={{ title: 'Certificate Application' }}
              >
                {/* Company selection — resume in-progress apps or start a new one */}
                {route('/applications/certificate', 'Certificate Application')}

                {/* General application routes */}
                {route(
                  '/applications/certificate/legal-disclaimer',
                  'Legal Disclaimer'
                )}
                {route('/applications/certificate/type', 'Application Type')}
                {/* Motor Carrier */}
                {route(
                  '/applications/certificate/motor-carrier',
                  'Motor Carrier Application'
                )}
                <Route
                  path={path(
                    '/applications/certificate/:applicationId/motor-carrier'
                  )}
                  element={<MotorCarrierStepsProvider />}
                  handle={{ title: 'Motor Carrier Application' }}
                >
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier',
                    'Motor Carrier Application'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/review-safer',
                    'Motor Carrier Application - Review SAFER'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/business-information',
                    'Motor Carrier Application - Business Structure'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/texas-agent',
                    'Motor Carrier Application - Texas Agent'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/interstate-ucr',
                    'Motor Carrier Application - Interstate UCR'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/registration-period',
                    'Motor Carrier Application - Registration Period'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/cargo-types',
                    'Motor Carrier Application - Cargo Types'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/vehicles',
                    'Motor Carrier Application - Vehicles'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/drug-test-certification',
                    'Motor Carrier Application - Drug Test Certification'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/questionnaire',
                    'Motor Carrier Application - Questionnaire'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/review',
                    'Motor Carrier Application - Review & Submit'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/motor-carrier/submitted',
                    'Motor Carrier Application - Submitted'
                  )}
                </Route>

                {/* Broker */}
                {route(
                  '/applications/certificate/broker',
                  'Broker Application'
                )}
                <Route
                  path={path('/applications/certificate/:applicationId/broker')}
                  element={<BrokerStepsProvider />}
                  handle={{ title: 'Broker Application' }}
                >
                  {index(
                    '/applications/certificate/:applicationId/broker',
                    'Broker Application'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/broker/review-safer',
                    'Broker Application'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/broker/documents',
                    'Broker Application'
                  )}
                  {route(
                    '/applications/certificate/:applicationId/broker/complete',
                    'Broker Application'
                  )}
                </Route>
              </Route>

              {/* Insurance Provider */}
              {route('/applications/insurance-provider')}
              {route('/applications/insurance-provider/complete')}

              {/* Licensing Agency */}
              {route('/applications/licensing-agency')}
              <Route
                path={path('/applications/licensing-agency/:agencyId')}
                element={<LicensingAgencyApplicationLayout />}
                handle={{ title: 'Licensing Agent Application' }}
              >
                {index(
                  '/applications/licensing-agency/:agencyId',
                  'Licensing Agent Application'
                )}
                {route(
                  '/applications/licensing-agency/:agencyId/business-info',
                  'Licensing Agent Application'
                )}
                {route(
                  '/applications/licensing-agency/:agencyId/documentation',
                  'Licensing Agent Application'
                )}
                {route(
                  '/applications/licensing-agency/:agencyId/certify',
                  'Licensing Agent Application'
                )}
                {route(
                  '/applications/licensing-agency/:agencyId/success',
                  'Licensing Agent Application'
                )}
              </Route>

              {/* Automated Motor Vehicle Application */}
              <Route
                path={path('/applications/automated-vehicles')}
                element={<AutonomousVehicleLayout />}
                handle={{ title: 'Automated Vehicle Application' }}
              >
                {index(
                  '/applications/automated-vehicles',
                  'Automated Vehicle Application'
                )}
                <Route element={<AutonomousVehicleStepGuard />}>
                  {route(
                    '/applications/automated-vehicles/:registrationId/business-info',
                    'Automated Vehicle Application'
                  )}
                  {route(
                    '/applications/automated-vehicles/:registrationId/contact-info',
                    'Automated Vehicle Application'
                  )}
                  {route(
                    '/applications/automated-vehicles/:registrationId/vehicles',
                    'Automated Vehicle Application'
                  )}
                  {route(
                    '/applications/automated-vehicles/:registrationId/certify',
                    'Automated Vehicle Application'
                  )}
                  {route(
                    '/applications/automated-vehicles/:registrationId/success',
                    'Automated Vehicle Application'
                  )}
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>
  )
)

function route(key: RouteKey, title?: string): React.ReactNode {
  return (
    <Route
      path={key}
      element={routes[key]}
      handle={title ? { title } : undefined}
    />
  )
}

function index(key: RouteKey, title?: string): React.ReactNode {
  return (
    <Route index element={routes[key]} handle={title ? { title } : undefined} />
  )
}

function path(key: RouteKey): string {
  return key
}

// Conditional DevTools import for dev environment ONLY
const DevTools = import.meta.env.DEV
  ? lazy(() =>
      import('./components/dev-tools/DevTools.tsx').then((module) => ({
        default: module.DevTools,
      }))
    )
  : null

const App = () => (
  <ErrorBoundary componentName="App">
    <ErrorProvider>
      <AppUpdateBanner />
      <AppConfigProvider>
        <AuthProvider>
          <Suspense
            fallback={<Loading variant="fullscreen" className="h-svh" />}
          >
            <RouterProvider router={router} />
          </Suspense>
          {DevTools ? <DevTools /> : null}
        </AuthProvider>
      </AppConfigProvider>
      <Toaster richColors closeButton />
    </ErrorProvider>
  </ErrorBoundary>
)

// RequireAuth helper to wrap protected routes.
// Checks authentication status and conditionally renders content.
function RequireAuth() {
  const { isAuthenticated, isLoading, user } = useAuth()

  // Loading authentication status, only if the user is not loaded yet (initial load)
  // All other loads of the user are background loads and should not cause UI flash
  if (isLoading && !user)
    return <Loading variant="fullscreen" className="h-svh" />

  // Not authenticated, show login page
  if (!isAuthenticated && !isLoading) return <Login />

  // Authenticated, render child routes and modals
  return (
    <>
      <Outlet />

      {/* Password change modal */}
      {isAuthenticated && user?.requiresPasswordChange && (
        <SetupPasswordModal />
      )}
    </>
  )
}

export default App
